import { Layout } from '~/layouts/default'
import { GetServerSideProps } from 'next'
import { useRouter } from 'next/router'
import MyTabs from '~/components/ui/MyTabs'
import { SEOHead } from '~/components/business/SEOHead'
import { PostScene, SinglePost } from '~/components/business/SinglePost'
import { GlobalRequest } from '~/components/global-requset'
import { MyContainer } from '~/components/ui/MyContainer'
import { MyEmpty } from '~/components/ui/MyEmpty'
import { PAGE_SIZE } from '~/configs'
import {
  CacheValueProps,
  getScrollCache,
  serverSideCatch,
  setScrollCache,
  toCDNImage,
} from '~/utils'
import { GET_TAGS, GET_SQUARE_AI_REGION_PAGE } from '~/graphqls/gqls'
import {
  PostTypes,
  PostQueryFilter,
  PostSortField,
  SortType,
  getTags,
  getTags_squareModule_squareOptionMenu,
  SquarePostType,
  getSquareAiRegionPage,
  getSquareAiRegionPageVariables,
  SquareItemType,
} from '~/graphqls/types'
import { useMasonry } from '~/hooks/useMasonry'
import { MyBtn } from '~/components/ui/MyBtn'
import useLayoutState from '~/hooks/useLayoutState'
import LayoutSwitch from '~/components/ui/LayoutSwitch'
import ThemeSelect from '~/components/pages/plaza/ThemeSelect'
import { useDidUpdateEffect } from '~/hooks/useDidUpdateEffect'
import { useEffect, useMemo, useRef, useState } from 'react'
import { MyIcon } from '~/components/ui/MyIcon'
import { useValidateAuth } from '~/hooks/useValidateAuth'
import useScrollTop from '~/hooks/useScrollTop'
import { useImperativeQuery } from '~/hooks/useImperativeQuery'
import useInfiniteScrollCursor from '~/hooks/useInfiniteScrollCursor'

const TabsList = [
  {
    id: '/plaza/follow',
    name: '关注',
  },
  {
    id: '/plaza',
    name: 'AI 广场',
  },
  {
    id: '/plaza/note',
    name: '动态',
  },
]

interface Props {
  hasNextPage: boolean
  list: PostTypes[]
  tagList: getTags_squareModule_squareOptionMenu
  scrollCache: CacheValueProps
  postType?: SquarePostType | null
  filterType?: PostQueryFilter
  endCursor: string
}

// 去除空数据
const getGraphData = (data: any[]) => {
  if (!data?.length) {
    return []
  }

  let newData: PostTypes[] = []
  data.forEach(item => {
    if (
      item.node?.type === SquarePostType.AI_COLLECTION &&
      (!item.node?.aiCollection?.aiArtworkCount ||
        item.node.post?.aiCollection?.aiArtworkCount < 4)
    ) {
      return
    }
    newData.push(item.node)
  })
  return newData
}
export default function Explorer({
  list: _list,
  hasNextPage: nextPage,
  endCursor: _endCursor,
  tagList,
  scrollCache,
  postType,
}: Props) {
  const scrollTop = useScrollTop()
  const [colCount, setColCount] = useLayoutState('plaza-layout', '6')
  const router = useRouter()
  const { validateIsLogin } = useValidateAuth()
  /** 选中的主题 */
  const themeid = Number(router.query.themeid) || 0
  const type =
    router.pathname === '/plaza'
      ? PostQueryFilter.HOME
      : PostQueryFilter.FOCUS_USER_AND_MYSELF
  const [endCursor, setEndCursor] = useState(_endCursor)

  // AI 创作 查询类型
  let acceptSquareType = useMemo(() => {
    let _acceptSquareType = [SquareItemType.AI_ARTWORK]
    if (themeid === -1) {
      _acceptSquareType = [SquareItemType.AI_COLLECTION]
    } else if (!themeid) {
      _acceptSquareType.push(SquareItemType.AI_COLLECTION)
    }
    return _acceptSquareType
  }, [themeid])

  const [loading, setLoading] = useState(false)

  let sortField =
    themeid === -1 ? PostSortField.CREATE_TIME : PostSortField.SCORE

  const [postList, setPostList] = useState(_list)
  const [hasNextPage, setHasNextPage] = useState(nextPage)

  const { InfiniteScollComp, list } = useInfiniteScrollCursor<
    PostTypes,
    getSquareAiRegionPage,
    getSquareAiRegionPageVariables
  >({
    queryDoc: GET_SQUARE_AI_REGION_PAGE,
    extraQueryParams: {
      param: {
        sortField: sortField,
        sort: SortType.DESC,
        createTags: !!themeid && themeid !== -1 ? [themeid] : undefined,
        type: postType,
        acceptSquareType,
      },
    },
    initData: postList || [],
    initPageSize: PAGE_SIZE,
    endCursor: endCursor,
    hasNextPage: hasNextPage,
    hasNextPageExtractor: data => {
      return (
        data.data?.squareModule?.squareAiRegionPage?.pageInfo?.hasNextPage ||
        false
      )
    },
    endCursorExtractor: res => {
      return (
        res.data.squareModule?.squareAiRegionPage?.pageInfo?.endCursor || ''
      )
    },
    dataExtractor: data => {
      return getGraphData(
        data.data?.squareModule?.squareAiRegionPage?.edges || []
      )
    },
  })

  const queryAiArtwork = useImperativeQuery<
    getSquareAiRegionPage,
    getSquareAiRegionPageVariables
  >(GET_SQUARE_AI_REGION_PAGE)

  const timer = useRef(0)

  useDidUpdateEffect(() => {
    window.clearTimeout(timer.current)
    setLoading(true)
    queryAiArtwork({
      first: PAGE_SIZE,
      param: {
        filter: type,
        sortField: sortField,
        sort: SortType.DESC,
        createTags: !!themeid && themeid !== -1 ? [themeid] : undefined,
        type: postType,
        acceptSquareType,
      },
    })
      .then(res => {
        setPostList(
          getGraphData(res?.data?.squareModule?.squareAiRegionPage?.edges || [])
        )
        setHasNextPage(
          res?.data?.squareModule?.squareAiRegionPage?.pageInfo?.hasNextPage ||
            false
        )
        setEndCursor(
          res.data.squareModule?.squareAiRegionPage?.pageInfo?.endCursor || ''
        )
      })
      .finally(() => {
        // 延迟动画，防止加载过快
        timer.current = window.setTimeout(() => {
          setLoading(false)
        }, 300)
      })
  }, [type, themeid, postType, sortField])

  const { columnList, columnWidth, columnGutter } = useMasonry<PostTypes>({
    data: list,
    sizeLoader: (it: PostTypes) => {
      if (!it?.coverImage?.height || !it?.coverImage?.width) return 1
      return it?.coverImage?.height / it?.coverImage?.width
    },
    column: Number(colCount),
    itemExtraHeight: 56,
  })

  const onTabsChange = (item: any) => {
    if (item.id === router.pathname) {
      return
    }
    if (item.id === TabsList[2].id) {
      router.push(`${item.id}?sortField=${PostSortField.CREATE_TIME}`)
    } else {
      router.push(item.id)
    }
  }

  useEffect(() => {
    window.scrollTo({ top: scrollCache?.scrollTop || 0, behavior: 'auto' })
  }, [])

  useDidUpdateEffect(() => {
    setScrollCache({
      key: router.asPath,
      value: {
        path: router.asPath,
        scrollTop,
        pageSize: list.length,
      },
    })
  }, [list.length, scrollTop, router.asPath])

  return (
    <Layout headerType="no-margin">
      <SEOHead
        title={TabsList.filter(it => it.id === router.pathname)?.[0]?.name}
        desc="分享你的AI创作，交流AI训练经验。"
      />
      <div
        className="w-full h-[152px] bg-cover bg-center relative"
        style={{
          backgroundImage: `url(${toCDNImage('/images/plaza/header-bg.jpg')})`,
        }}
      >
        <div
          className="absolute top-0 right-0 left-0 bottom-0"
          style={{
            background:
              'linear-gradient(180deg, rgba(0, 0, 0, 0.1) 0%, rgba(0, 0, 0, 0.79) 100%)',
          }}
        >
          <MyContainer className="flex items-end justify-between h-full !pb-6">
            <div className="text-neutral-900">
              <div className="text-[40px] leading-[48px] font-semibold">
                AI 广场
              </div>
            </div>
            <div>
              <MyBtn
                type="primary"
                onClick={() => {
                  if (validateIsLogin()) {
                    router.push('/ai/community/select-ai')
                  }
                }}
              >
                <MyIcon icon="fabuchuangzuo" className="mr-2 align-text-top" />
                发布 AI 创作
              </MyBtn>
              <MyBtn
                type="primary"
                className="!bg-[#F9DEB9] !border-[#F9DEB9] !text-neutral-50 ml-3 "
                onClick={() => {
                  router.push('/rank')
                }}
              >
                <MyIcon icon="paihangbang" className="mr-2 align-text-top" />
                排行榜
              </MyBtn>
            </div>
          </MyContainer>
        </div>
      </div>
      <MyContainer className="!pt-6">
        <div className="flex items-center justify-between sticky top-14 z-[998] bg-neutral-100 mb-[25px]">
          <MyTabs
            className="text-base"
            items={TabsList}
            onChange={onTabsChange}
            active={router.pathname}
          />
          <div className="flex">
            <LayoutSwitch col={colCount} setCol={setColCount} />
          </div>
        </div>

        {type === PostQueryFilter.HOME && (
          <div className="mb-8">
            <ThemeSelect tagList={(tagList.createTagOptions as any) || []} />
          </div>
        )}

        {list.length > 0 ? (
          <>
            <div className="flex justify-between items-start">
              {columnList?.map((item, index) => (
                <div
                  style={{
                    width: columnWidth,
                  }}
                  key={index}
                >
                  {item?.map(it => (
                    <div
                      key={it.id}
                      style={{
                        marginBottom: columnGutter,
                      }}
                    >
                      <SinglePost
                        post={it}
                        scene={PostScene.RESPONSIVE_NORMAL}
                        loading={loading}
                      />
                    </div>
                  ))}
                </div>
              ))}
            </div>
            {InfiniteScollComp}
          </>
        ) : (
          <MyEmpty />
        )}
      </MyContainer>
    </Layout>
  )
}

export const getServerSideProps: GetServerSideProps = async context => {
  return await serverSideCatch(async () => {
    const { initialState, client } = await GlobalRequest(context)
    const scrollCache = getScrollCache(context)

    /** 选中的主题 */
    const themeid = Number(context.query.themeid) || 0

    let acceptSquareType = [SquareItemType.AI_ARTWORK]
    if (themeid === -1) {
      acceptSquareType = [SquareItemType.AI_COLLECTION]
    }
    if (!themeid) {
      acceptSquareType.push(SquareItemType.AI_COLLECTION)
    }
    const [res, tagRes] = await Promise.all([
      client.query<getSquareAiRegionPage, getSquareAiRegionPageVariables>({
        query: GET_SQUARE_AI_REGION_PAGE,
        variables: {
          param: {
            filter: PostQueryFilter.HOME,
            sortField:
              themeid === -1 ? PostSortField.CREATE_TIME : PostSortField.SCORE,
            sort: SortType.DESC,
            createTags: !!themeid && themeid !== -1 ? [themeid] : undefined,
            acceptSquareType,
          },
          first: scrollCache.pageSize || PAGE_SIZE,
        },
        fetchPolicy: 'no-cache',
      }),
      client.query<getTags>({
        query: GET_TAGS,
        fetchPolicy: 'no-cache',
      }),
    ])

    return {
      props: {
        initialState,
        scrollCache,
        hasNextPage:
          res?.data?.squareModule?.squareAiRegionPage?.pageInfo?.hasNextPage ||
          false,
        endCursor:
          res?.data?.squareModule?.squareAiRegionPage?.pageInfo?.endCursor,
        list: getGraphData(
          res?.data?.squareModule?.squareAiRegionPage?.edges || []
        ),
        tagList: tagRes.data.squareModule?.squareOptionMenu,
        postType: SquarePostType.AI_ARTWORK,
      },
    }
  })
}
